'use client';

import { useState, useRef } from 'react';
import Image from 'next/image';
import { useTranslations } from 'next-intl';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/shared/ui/shadcn/dialog';
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
  DrawerDescription,
} from '@/shared/ui/shadcn/drawer';
import { useMediaQuery } from '@/shared/hooks/use-media-query';
import { Upload, Sparkles } from 'lucide-react';
import { cn } from '@/shared/lib/cn';
import {
  RenderPickerModal,
  type MediaSourceConfig,
} from '@/features/render-picker';
import {
  FileUploadDropZone,
  type FileUploadDropZoneRef,
} from './FileUploadDropZone';

export interface MediaSourceExtraOption {
  icon: React.ReactNode;
  title: string;
  onClick: () => void;
}

/**
 * Thumbnail item for the optional "Project angles" gallery shown at the top
 * of the selector. Pre-resolved so the selector stays presentational.
 */
export interface MediaSourceProjectAngle {
  id: string;
  fileUrl: string | null;
  name: string | null;
}

interface MediaSourceSelectorProps {
  isOpen: boolean;
  onClose: () => void;
  /** User abandoned the flow without picking anything: dismissed the sheet or
   *  dialog (swipe/overlay/esc), or cancelled the native file dialog after
   *  choosing "Upload from device". Lets owners of transient mount-and-autoOpen
   *  flows reset their `open` state — without it the flow gets stuck and the
   *  selector can never be reopened. */
  onDismiss?: () => void;
  onFilesSelected: (files: File[]) => void;
  config?: MediaSourceConfig;
  /** Additional options shown after upload (e.g. "Select from library") */
  extraOptions?: MediaSourceExtraOption[];
  /** Optional gallery of existing project angles, rendered as a horizontal
   *  thumbnail strip above the source-option cards. Mirrors Apple's
   *  `ImageSourceSheet` "Project angles" section. */
  projectAngles?: MediaSourceProjectAngle[];
  /** Tap on a thumbnail → attach the angle in the consumer's flow. */
  onProjectAngleSelected?: (angleId: string) => void;
  /** Classes for the selector's own layer (sheet/dialog + its overlay). A caller
   *  rendered above the default z-50 stack — the fullscreen sketch editor sits
   *  at z-index 100000 — must raise the selector with it, or the sheet opens
   *  behind that opaque layer and the button reads as dead. */
  contentClassName?: string;
  overlayClassName?: string;
}

/**
 * Dialog for selecting media source: upload from device or use existing render
 */
export function MediaSourceSelector({
  isOpen,
  onClose,
  onDismiss,
  onFilesSelected,
  config = {},
  extraOptions,
  projectAngles,
  onProjectAngleSelected,
  contentClassName,
  overlayClassName,
}: MediaSourceSelectorProps) {
  const t = useTranslations('mediaSource');

  const {
    showRendersOption = true,
    allowMultiple = false,
    maxFiles,
    accept = 'image/*',
  } = config;

  const [showRenderPicker, setShowRenderPicker] = useState(false);
  const fileInputRef = useRef<FileUploadDropZoneRef>(null);
  const isMobile = !useMediaQuery('(min-width: 768px)');

  const handleUploadClick = () => {
    onClose();
    setTimeout(() => {
      fileInputRef.current?.openFileDialog();
    }, 100);
  };

  const handleRendersClick = () => {
    setShowRenderPicker(true);
  };

  const handleRenderSelected = (file: File) => {
    setShowRenderPicker(false);
    onClose();
    onFilesSelected([file]);
  };

  const handleFilesFromDropZone = (files: File[]) => {
    onFilesSelected(files);
  };

  const isVisible = isOpen && !showRenderPicker;

  const hasProjectAngles =
    !!projectAngles && projectAngles.length > 0 && !!onProjectAngleSelected;

  const projectAnglesSection = hasProjectAngles ? (
    <section className="pt-2">
      <h3 className="text-list-item-subtitle text-muted-foreground mb-2">
        {t('projectAngles')}
      </h3>
      <div className="-mx-1 overflow-x-auto">
        <div className="flex gap-2 px-1 pb-1">
          {projectAngles!.map((angle) => (
            <ProjectAngleThumb
              key={angle.id}
              angle={angle}
              onClick={() => {
                onClose();
                onProjectAngleSelected!(angle.id);
              }}
            />
          ))}
        </div>
      </div>
    </section>
  ) : null;

  const optionsContent = (
    <div className="grid gap-3 pt-2 pb-3">
      <SourceOptionCard
        icon={<Upload className="w-5 h-5" />}
        title={t('uploadFiles')}
        onClick={handleUploadClick}
      />

      {showRendersOption && (
        <SourceOptionCard
          icon={<Sparkles className="w-5 h-5" />}
          title={t('useRenders')}
          onClick={handleRendersClick}
        />
      )}

      {extraOptions?.map((option, index) => (
        <SourceOptionCard
          key={index}
          icon={option.icon}
          title={option.title}
          onClick={() => {
            onClose();
            option.onClick();
          }}
        />
      ))}
    </div>
  );

  return (
    <>
      {isMobile ? (
        // vaul defaults autoFocus=false and force-preventDefaults Radix's
        // onOpenAutoFocus, so the trigger button keeps focus while vaul wraps
        // the layout root in aria-hidden — Chrome then logs
        // "aria-hidden on focused ancestor". autoFocus moves focus to the
        // first sheet option, which is what Radix Dialog already does on desktop.
        // onOpenChange fires only on user-initiated closes (swipe/overlay/esc),
        // never on programmatic `open` flips — so onDismiss here can't fire
        // when an option click closes the sheet on its way into a sub-flow.
        <Drawer
          open={isVisible}
          onOpenChange={(open) => {
            if (!open) {
              onClose();
              onDismiss?.();
            }
          }}
          autoFocus
        >
          <DrawerContent
            className={contentClassName}
            overlayClassName={overlayClassName}
          >
            <DrawerHeader>
              <DrawerTitle>{config.title || t('title')}</DrawerTitle>
              {config.subtitle && (
                <DrawerDescription>{config.subtitle}</DrawerDescription>
              )}
            </DrawerHeader>
            <div className="px-4 pb-4">
              {projectAnglesSection}
              {optionsContent}
            </div>
          </DrawerContent>
        </Drawer>
      ) : (
        <Dialog
          open={isVisible}
          onOpenChange={(open) => {
            if (!open) {
              onClose();
              onDismiss?.();
            }
          }}
        >
          <DialogContent
            className={cn('max-w-md', contentClassName)}
            overlayClassName={overlayClassName}
          >
            <DialogHeader>
              <DialogTitle>{config.title || t('title')}</DialogTitle>
              {config.subtitle && (
                <DialogDescription>{config.subtitle}</DialogDescription>
              )}
            </DialogHeader>
            {projectAnglesSection}
            {optionsContent}
          </DialogContent>
        </Dialog>
      )}

      <div className="hidden">
        <FileUploadDropZone
          ref={fileInputRef}
          onFilesSelected={handleFilesFromDropZone}
          onCancel={onDismiss}
          accept={accept}
          multiple={allowMultiple}
          maxFiles={maxFiles}
        />
      </div>

      {showRendersOption && (
        <RenderPickerModal
          isOpen={showRenderPicker}
          onClose={() => setShowRenderPicker(false)}
          onRenderSelected={handleRenderSelected}
          contentClassName={contentClassName}
          overlayClassName={overlayClassName}
        />
      )}
    </>
  );
}

interface ProjectAngleThumbProps {
  angle: MediaSourceProjectAngle;
  onClick: () => void;
}

function ProjectAngleThumb({ angle, onClick }: ProjectAngleThumbProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="group flex flex-col items-center gap-1 shrink-0 w-20"
      aria-label={angle.name ?? 'angle'}
    >
      <div className="relative w-20 h-20 rounded-xl overflow-hidden bg-muted ring-1 ring-border/50 group-active:ring-primary transition-colors">
        {angle.fileUrl ? (
          <Image
            src={angle.fileUrl}
            alt=""
            fill
            sizes="80px"
            className="object-cover"
          />
        ) : null}
      </div>
      {angle.name && (
        <span className="text-card-meta text-muted-foreground truncate w-full text-center">
          {angle.name}
        </span>
      )}
    </button>
  );
}

interface SourceOptionCardProps {
  icon: React.ReactNode;
  title: string;
  onClick: () => void;
}

function SourceOptionCard({ icon, title, onClick }: SourceOptionCardProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'flex items-center gap-4 p-4 rounded-xl w-full text-start',
        'bg-muted-foreground/[0.08] border border-muted-foreground/[0.15]',
        'hover:bg-muted-foreground/[0.12] active:bg-muted-foreground/[0.16]',
        'transition-colors duration-150',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring'
      )}
    >
      <div className="flex-shrink-0 w-7 flex items-center justify-center text-primary">
        {icon}
      </div>
      <div className="flex-1 min-w-0">
        <p className="font-medium text-foreground text-[15px] leading-tight">
          {title}
        </p>
      </div>
    </button>
  );
}
